import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { ReactNode, useState } from "react";
import { createPortal } from "react-dom";

import { BottomSheetView } from "../BottomSheet/BottomSheetView";
import { Button } from "../Button/Button";
import { SelectableRow, TierName } from "../SelectableRow/SelectableRow";
import CloseButton from "../SpecificButtons/CloseButton/CloseButton";

export type RoleOption = {
  id: number | string;
  name: string;
  /**
   * Optional meta — omit both where they don't apply (e.g. picking a role to
   * grade multiple players by); the role stays selectable without them.
   */
  rating?: string | number;
  tier?: TierName;
  /** Green "++" chem indicator. */
  plus?: boolean;
  /** Not selectable (e.g. the player can't play it). */
  disabled?: boolean;
};

export type RolePositionGroup = {
  position: string;
  roles: RoleOption[];
};

export type RoleSelectPanelProps = {
  /** Current selection on the trigger button (e.g. "RW · Winger"). */
  triggerLabel: ReactNode;
  groups: RolePositionGroup[];
  /** id of the selected role; undefined means "Best" is selected. */
  selectedRoleId?: RoleOption["id"];
  /** Called with the picked role, or undefined when "Best" is picked. */
  onSelect: (role?: RoleOption) => void;
  title?: string;
  subtitle?: ReactNode;
  bestLabel?: string;
  bestHint?: string;
  /**
   * "panel" (default) anchors a grid panel to the trigger — desktop. "sheet"
   * opens a BottomSheet of chip options — mobile / inside other sheets.
   */
  presentation?: "panel" | "sheet";
  /** Bottom ad reserve for the "sheet" presentation (see RoleSelectSheetContent). */
  paddingBottomClassName?: string;
};

/**
 * The sheet body of the role picker: title row with close button, the "Best"
 * chip, every position group as a chip grid and the "Show all positions"
 * toggle. Rendered by RoleSelectPanel's "sheet" presentation, and reusable
 * standalone inside other sheet chromes (e.g. the mobile compare view's
 * in-frame sheet).
 */
export const RoleSelectSheetContent = ({
  groups,
  selectedRoleId,
  onPick,
  onClose,
  title,
  subtitle,
  bestLabel,
  bestHint,
  paddingBottomClassName,
  multi = false,
  selectedRoleIds = [],
  onToggle,
}: {
  groups: RolePositionGroup[];
  selectedRoleId?: RoleOption["id"];
  /** Called with the picked role, or undefined when "Best" is picked. */
  onPick: (role?: RoleOption) => void;
  /**
   * Several roles at once: chips toggle through `onToggle` and "Best" clears
   * them (through `onPick(undefined)`).
   */
  multi?: boolean;
  selectedRoleIds?: RoleOption["id"][];
  onToggle?: (role: RoleOption) => void;
  onClose: () => void;
  title?: string;
  subtitle?: ReactNode;
  bestLabel?: string;
  bestHint?: string;
  /**
   * Bottom ad reserve: the sheet scrolls to its end, so without this padding
   * the last rows sit under the anchored ad. Supplied by the connected
   * RoleSelectPanel wrapper; omitted here so this module stays Redux-free.
   */
  paddingBottomClassName?: string;
}) => {
  const { t } = useTranslation("players");
  // Default to every position; "Show fewer" keeps only those with a pickable role.
  const [showAll, setShowAll] = useState(false);

  const enabledGroups = groups
    .map((g) => ({ ...g, roles: g.roles.filter((r) => !r.disabled) }))
    .filter((g) => g.roles.length > 0);
  const filterHasEffect = groups.some((g) => g.roles.some((r) => r.disabled));
  const visibleGroups = showAll ? groups : enabledGroups;
  const bestSelected = multi
    ? selectedRoleIds.length === 0
    : selectedRoleId == null;
  const isSelected = (id: RoleOption["id"]) =>
    multi ? selectedRoleIds.includes(id) : id === selectedRoleId;

  return (
    <div
      className={cn("flex flex-col gap-4 px-6 py-2", paddingBottomClassName)}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[17px] font-semibold text-neutral-100">
            {title ?? t("roleSelect.title")}
          </div>
          {subtitle && (
            <div className="mt-[3px] text-[12.5px] text-foreground-muted">
              {subtitle}
            </div>
          )}
        </div>
        <CloseButton className="shrink-0" onClick={onClose} />
      </div>
      {/* Same Best row as the desktop panel: label + hint left, "Selected"
          right, blue tint + inset ring when active. */}
      <button
        type="button"
        onClick={() => onPick(undefined)}
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-[10px] px-3 py-2.5",
          bestSelected
            ? "bg-blue-900/50 shadow-[inset_0_0_0_1px_var(--color-primary-border)]"
            : "bg-neutral-750",
        )}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="shrink-0 text-[13.5px] font-semibold text-neutral-100">
            {bestLabel ?? t("roleSelect.bestLabel")}
          </span>
          <span className="truncate text-[11.5px] text-foreground-muted">
            {bestHint ?? t("roleSelect.bestHint")}
          </span>
        </span>
        {bestSelected && (
          <span className="shrink-0 text-[13px] text-neutral-100">
            {t("roleSelect.selected")}
          </span>
        )}
      </button>
      {visibleGroups.map((group) => (
        <div key={group.position}>
          <div className="mb-2 text-xs font-semibold tracking-[0.06em] text-foreground-muted">
            {group.position}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {group.roles.map((role) => (
              <SelectableRow
                key={role.id}
                label={role.name}
                plus={role.plus}
                selected={isSelected(role.id)}
                disabled={role.disabled}
                onSelect={() =>
                  multi && onToggle ? onToggle(role) : onPick(role)
                }
              />
            ))}
          </div>
        </div>
      ))}
      {filterHasEffect && (
        <Button
          variant="secondary"
          size="small"
          className="ml-auto"
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll
            ? t("roleSelect.showFewer")
            : t("roleSelect.showAllPositions")}
        </Button>
      )}
    </div>
  );
};

/**
 * The Select Player Role picker: a trigger button opening every position and
 * its roles, plus a "Best" option. Picking one closes it.
 */
export const RoleSelectPanel = ({
  triggerLabel,
  groups,
  selectedRoleId,
  onSelect,
  title,
  subtitle,
  bestLabel,
  bestHint,
  presentation = "panel",
  paddingBottomClassName,
}: RoleSelectPanelProps) => {
  const { t } = useTranslation("players");
  const [open, setOpen] = useState(false);
  // Default to every position; "Show fewer" keeps only those with a pickable role.
  const [showAll, setShowAll] = useState(false);
  const ref = useOutsideClick(() => setOpen(false));
  useEscape(() => setOpen(false));

  const bestSelected = selectedRoleId == null;
  const pick = (role?: RoleOption) => {
    onSelect(role);
    setOpen(false);
  };

  // "Show fewer" hides every disabled role (and drops groups left empty). The
  // toggle only earns its footer when there is something to hide.
  const enabledGroups = groups
    .map((g) => ({ ...g, roles: g.roles.filter((r) => !r.disabled) }))
    .filter((g) => g.roles.length > 0);
  const filterHasEffect = groups.some((g) => g.roles.some((r) => r.disabled));
  const visibleGroups = showAll ? groups : enabledGroups;

  const trigger = (
    <Button
      variant="secondary"
      size="small"
      onClick={() => setOpen((prev) => !prev)}
    >
      {triggerLabel}
    </Button>
  );

  if (presentation === "sheet") {
    // Portaled to <body>: when the trigger lives inside another BottomSheet, a
    // transformed ancestor (translate-y) would otherwise become the containing
    // block for our fixed sheet and trap it there.
    return (
      <>
        {trigger}
        {createPortal(
          <BottomSheetView
            isOpen={open}
            onClose={() => setOpen(false)}
            className="h-[85dvh]"
          >
            <RoleSelectSheetContent
              groups={groups}
              selectedRoleId={selectedRoleId}
              onPick={pick}
              onClose={() => setOpen(false)}
              title={title}
              subtitle={subtitle}
              bestLabel={bestLabel}
              bestHint={bestHint}
              paddingBottomClassName={paddingBottomClassName}
            />
          </BottomSheetView>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div className="relative" ref={ref}>
      {trigger}
      {open && (
        <div
          className={cn(
            "absolute right-0 top-[calc(100%+10px)] z-60 flex max-h-[72vh] w-[min(980px,88vw)] flex-col overflow-hidden",
            "rounded-2xl border border-neutral-700 bg-neutral-800",
            "shadow-[0_24px_60px_rgba(0,0,0,0.6)]",
            "transition-all duration-150 ease-out",
            "translate-y-0 scale-100 opacity-100",
            "starting:-translate-y-1.5 starting:scale-[0.985] starting:opacity-0",
          )}
        >
          {/* Sticky header */}
          <div className="flex items-start justify-between gap-4 border-b border-neutral-700 px-5 pb-3.5 pt-[18px]">
            <div>
              <div className="text-[17px] font-semibold text-neutral-100">
                {title ?? t("roleSelect.title")}
              </div>
              {subtitle && (
                <div className="mt-[3px] text-[12.5px] text-foreground-muted">
                  {subtitle}
                </div>
              )}
            </div>
            <div className="-mr-1 -mt-1 shrink-0">
              <CloseButton onClick={() => setOpen(false)} />
            </div>
          </div>

          {/* Scroll body */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-[18px] pt-4">
            <button
              type="button"
              onClick={() => pick(undefined)}
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-[10px] px-3 py-2.5",
                bestSelected
                  ? "bg-blue-900/50 shadow-[inset_0_0_0_1px_var(--color-primary-border)]"
                  : "bg-neutral-750",
              )}
            >
              <span className="flex items-center gap-2.5">
                <span className="text-[13.5px] font-semibold text-neutral-100">
                  {bestLabel ?? t("roleSelect.bestLabel")}
                </span>
                <span className="text-[11.5px] text-foreground-muted">
                  {bestHint ?? t("roleSelect.bestHint")}
                </span>
              </span>
              {bestSelected && (
                <span className="text-[13px] text-neutral-100">
                  {t("roleSelect.selected")}
                </span>
              )}
            </button>

            <div className="mt-4 grid grid-cols-4 gap-x-4 gap-y-3.5">
              {visibleGroups.map((group) => (
                <div key={group.position} className="min-w-0">
                  <div className="mb-2 text-xs font-semibold tracking-[0.06em] text-foreground-muted">
                    {group.position}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {group.roles.map((role) => (
                      <SelectableRow
                        key={role.id}
                        label={role.name}
                        plus={role.plus}
                        rating={role.rating}
                        tier={role.tier}
                        selected={role.id === selectedRoleId}
                        disabled={role.disabled}
                        onSelect={() => pick(role)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sticky footer */}
          {filterHasEffect && (
            <div className="flex flex-row-reverse items-center justify-between gap-3 border-t border-neutral-700 bg-neutral-800 px-5 py-3">
              <Button
                variant="secondary"
                size="small"
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll
                  ? t("roleSelect.showFewer")
                  : t("roleSelect.showAllPositions")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
